import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpenIcon } from 'lucide-react';
import {
  Button,
  CheckList,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  Heading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import {
  RESOURCES_QUERY_KEY,
  RESOURCE_AUDIENCE,
  RESOURCE_STATUS,
  fileSize,
  listOrganizationResources,
  listResources,
  organizationResourcesQueryKey,
  shareResources,
  shortDate,
  type OrganizationResource,
} from '@/lib/resources';
import { ResourceFormatIcon } from '@/pages/resource-format-icon';
import { FOR_ORGANIZATION_PARAM } from '@/pages/ResourceEditorPage';

const PAGE_SIZE = 25;

const SORT_FIELDS = {
  resource: 'title',
  topic: 'topicName',
  alsoShared: 'alsoSharedLabel',
  views: 'organizationViews',
  lastOpened: 'lastOpenedSort',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
/** Most recently opened first: what this client is reading leads. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.lastOpened, direction: 'desc' };
/** The views column, by position. */
const RIGHT_ALIGNED = [3] as const;

function detailLine(resource: OrganizationResource): string {
  return resource.file ? `${resource.file.kind} · ${fileSize(resource.file.size)}` : resource.formatLine;
}

/**
 * An organization's Resources tab (Roy's portal list, item 13): what this
 * client gets beyond the every-client library, how often its people open each
 * one, and two ways in: write a resource just for them, or share one that
 * exists. Taking a client off a resource happens in the editor's "Who sees
 * it", so there is one place to change an audience.
 *
 * @param props - The organization.
 * @returns The tab.
 */
export function OrgResourcesTab({
  organizationId,
  organizationName,
}: {
  readonly organizationId: string;
  readonly organizationName: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const resources = useQuery({
    queryKey: organizationResourcesQueryKey(organizationId),
    queryFn: ({ signal }) => listOrganizationResources(organizationId, signal),
  });

  const headers: readonly DataTableHeader[] = [
    { label: 'Resource', sortKey: SORT_FIELDS.resource },
    { label: 'Topic', sortKey: SORT_FIELDS.topic },
    { label: 'Also shared with', sortKey: SORT_FIELDS.alsoShared },
    { label: `${organizationName} views`, sortKey: SORT_FIELDS.views },
    { label: 'Last opened', sortKey: SORT_FIELDS.lastOpened },
  ];
  const all = resources.data ?? [];
  const needle = search.trim().toLowerCase();
  const rows = sortRows(
    all
      .map((resource) => ({
        ...resource,
        topicName: resource.topic?.name ?? '',
        alsoSharedLabel:
          resource.alsoSharedWith.length === 0
            ? `Only ${organizationName}`
            : resource.alsoSharedWith.map((organization) => organization.name).join(', '),
        lastOpenedSort: resource.lastOpenedAt ?? '',
      }))
      .filter((resource) => needle === '' || `${resource.title} ${resource.topicName} ${resource.tags.join(' ')}`.toLowerCase().includes(needle)),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const addForThem = `/resources/new?${FOR_ORGANIZATION_PARAM}=${organizationId}`;

  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading variant="section">Only for {organizationName}</Heading>
          <p className="mt-1 text-portal-small text-portal-muted">
            Resources shared with this client. Everything shared with every client shows in their Resource center too.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button guide="org-resources-share" variant="secondary" onClick={() => setSharing(true)}>
            Share an existing resource
          </Button>
          <Button guide="org-resources-add" onClick={() => navigate(addForThem)}>Add a resource for {organizationName}</Button>
        </div>
      </div>

      {resources.isError ? (
        <ErrorNotice title="Couldn’t load this client’s resources" message={errorMessage(resources.error)} />
      ) : resources.isPending ? (
        <SkeletonTable rows={4} columns={['w-[38%]', 'w-[18%]', 'w-[18%]', 'w-[10%]', 'w-[12%]']} label="Loading resources…" />
      ) : all.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<BookOpenIcon aria-hidden className="size-6" />}
            title={`Nothing just for ${organizationName} yet`}
            message={`${organizationName} sees every resource shared with every client. Add one written for them, or share one from the library, and it shows under “For ${organizationName}” at the top of their Resource center.`}
            action={<Button onClick={() => navigate(addForThem)}>Add a resource for {organizationName}</Button>}
          />
        </Panel>
      ) : (
        <>
          <SearchInput
            label={`Search ${organizationName}’s resources`}
            placeholder="A title, a topic, a tag"
            className="mb-4 w-full max-w-sm"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          {rows.length === 0 ? (
            <Panel>
              <EmptyState icon={<BookOpenIcon aria-hidden className="size-6" />} title="No resources match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable guide="org-resources-table" headers={headers} rightAlignedHeaders={RIGHT_ALIGNED} {...(sort === undefined ? {} : { sort })} onSort={onSort}>
              {shown.map((resource) => (
                <tr key={resource.id} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <div className="flex items-center gap-3">
                      <ResourceFormatIcon format={resource.format} />
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => navigate(`/resources/${resource.id}?${FOR_ORGANIZATION_PARAM}=${organizationId}`)}
                          className="text-left font-bold text-portal-ink underline-offset-2 hover:text-brand hover:underline"
                        >
                          {resource.title}
                        </button>
                        <span className="block text-portal-micro text-portal-muted">{detailLine(resource)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{resource.topicName}</td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    <span className="flex flex-wrap items-center gap-2">
                      {resource.alsoSharedLabel}
                      {resource.status === RESOURCE_STATUS.draft ? <Pill tone="warning">Draft</Pill> : null}
                    </span>
                  </td>
                  <td className="p-3 text-right text-portal-small whitespace-nowrap">{resource.organizationViews.toLocaleString('en-US')}</td>
                  <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
                    {resource.lastOpenedAt ? shortDate(resource.lastOpenedAt) : 'Not yet'}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
          {totalPages > 1 ? (
            <div className="mt-4">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}

      <ShareResourceDrawer
        open={sharing}
        organizationId={organizationId}
        organizationName={organizationName}
        onClose={() => setSharing(false)}
      />
    </div>
  );
}

/**
 * "Share an existing resource": the library minus what this client already
 * sees through every-client sharing, with what is already theirs ticked off.
 */
function ShareResourceDrawer({
  open,
  organizationId,
  organizationName,
  onClose,
}: {
  readonly open: boolean;
  readonly organizationId: string;
  readonly organizationName: string;
  readonly onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const library = useQuery({ queryKey: RESOURCES_QUERY_KEY, queryFn: ({ signal }) => listResources(signal), enabled: open });

  const close = (): void => {
    setPicked(new Set());
    setSearch('');
    onClose();
  };
  const share = useMutation({
    mutationFn: () => shareResources(organizationId, [...picked]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organizationResourcesQueryKey(organizationId) });
      await queryClient.invalidateQueries({ queryKey: RESOURCES_QUERY_KEY });
      close();
    },
  });

  const needle = search.trim().toLowerCase();
  const candidates = (library.data ?? [])
    .filter((resource) => resource.status !== RESOURCE_STATUS.archived && resource.audience.scope === RESOURCE_AUDIENCE.chosenClients)
    .filter((resource) => needle === '' || `${resource.title} ${resource.topic?.name ?? ''} ${resource.tags.join(' ')}`.toLowerCase().includes(needle))
    .sort((a, b) => a.title.localeCompare(b.title));
  const options = candidates.map((resource) => {
    const already = resource.audience.organizations.some((organization) => organization.id === organizationId);
    return {
      key: resource.id,
      label: resource.title,
      detail: [resource.topic?.name, resource.file?.kind ?? resource.formatLine.split(' · ')[0]].filter(Boolean).join(' · '),
      ...(already ? { value: 'Shared', disabled: true } : {}),
    };
  });

  return (
    <Drawer
      open={open}
      size="wide"
      title={`Share an existing resource with ${organizationName}`}
      subtitle={`Pick from the library. It appears under “For ${organizationName}” as soon as you share it.`}
      onClose={() => {
        if (!share.isPending) close();
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button guide="share-resources-submit" disabled={picked.size === 0 || share.isPending} onClick={() => share.mutate()}>
            {share.isPending ? 'Sharing…' : `Share with ${organizationName}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <SearchInput label="Search the library" placeholder="A title, a topic, a tag" value={search} onChange={(event) => setSearch(event.target.value)} />
        {library.isError ? (
          <ErrorNotice title="Couldn’t load the library" message={errorMessage(library.error)} />
        ) : library.isPending ? (
          <SkeletonTable rows={4} columns={['w-[70%]', 'w-[20%]']} label="Loading the library…" />
        ) : options.length === 0 ? (
          <EmptyState
            icon={<BookOpenIcon aria-hidden className="size-6" />}
            title={needle === '' ? 'Nothing to share yet' : 'No resources match'}
            message={
              needle === ''
                ? 'Every resource in the library already goes to every client. Add one for this client instead.'
                : 'Try a different search.'
            }
          />
        ) : (
          <div data-guide="share-resources-list"><CheckList<string>
            label={`Resources to share with ${organizationName}`}
            options={options}
            values={picked}
            onToggle={(key, checked) =>
              setPicked((current) => {
                const next = new Set(current);
                if (checked) next.add(key);
                else next.delete(key);
                return next;
              })
            }
          /></div>
        )}
        <p className="text-portal-micro text-portal-muted">
          Resources shared with every client are not listed: {organizationName} already sees them.
        </p>
        {share.isError ? <ErrorNotice inline title="Not shared" message={errorMessage(share.error)} /> : null}
      </div>
    </Drawer>
  );
}
